import { omit } from 'lodash'
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { v4 as uuidv4 } from 'uuid'

import EnvelopesService from '@services/envelopes'
import ShelfService from '@services/shelf'
import BoxesService from '@services/boxes'

import { setPagination } from '@store/modules/pagination'
import { IEnvelopeTemplateGeneratedDocument } from '@store/modules/boxes/types'
import {
  catchAxiosError,
  getPagination,
  getDraftEnvelopeData,
  normalizeEnvelopeFieldValueForRequest
} from '@utils/utils'
import { DraftEnvelopeRecipientsBaseColors, EnvelopeDirection } from '@const/consts'
import { ACTION_REQUIRED_COUNTS_UPDATED_EVENT } from '@const/events'
import { renderPdfPreview } from '@views/envelopes/components/NewEnvelope/pdfPreview'
import { getStageRouteKey } from '@utils/routeKeys'

import { AppState } from '@store/types'
import {
  DraftEnvelopeRecipientSignerType,
  DraftEnvelopeRecipientType,
  IDraftEnvelope,
  IDraftEnvelopeCommentFile,
  IDraftEnvelopeField,
  IEnvelopeFilePreview,
  IDraftEnvelopeMessage,
  IDraftEnvelopePrepositionedMark,
  IDraftEnvelopeRecipient,
  IEnvelope,
  IEnvelopeFields,
  IEnvelopeParams,
  IEnvelopesListParams,
  IEnvelopesState,
  IGetPreviewParams,
  IUploadFileParams,
  ISetDraftEnvelopePrepositionedMark
} from './types'
import { Checkbox } from '@hooks/envelopes/types'
import { Nullable } from '@app/types'

const initialState: IEnvelopesState = {
  envelopes: {
    data: [],
    direction: undefined
  },
  envelopesListCache: {},
  checkedEnvelopes: {},
  isAllEnvelopesSelected: false,
  currentEnvelope: null,
  currentBoxOguid: '',
  currentUserFilterOguid: null,
  draftEnvelope: {
    commentFiles: [],
    fields: {},
    fieldsIdList: [],
    envelopeTemplate: null,
    files: [],
    generatedTemplateFiles: null,
    generatedTemplateFilesSubject: null,
    labels: [],
    notifications: null,
    message: {
      comment: null,
      expirationDateTime: null,
      externalId: null,
      subject: ''
    },
    prepositionedMarks: {},
    prepositionedMarksIdList: [],
    preview: {},
    recipients: {},
    recipientsColors: DraftEnvelopeRecipientsBaseColors,
    recipientsIdList: [],
    templateFiles: null,
    variablesIdList: []
  },
}

interface IGeneratedTemplateDocuments {
  files: string[]
  generatedFiles: IEnvelopeTemplateGeneratedDocument[]
}

interface ISendEnvelopeParams {
  boxOguid: string
  files?: string[]
}

interface ISetDraftEnvelopePreviewPayload {
  fileUuid: string
  preview: IEnvelopeFilePreview
}

interface ISetEnvelopeReadStatePayload {
  boxOguid: string
  envelopeOguid: string
  isRead: boolean
}

const GENERATED_TEMPLATE_FILE_EXTENSION = '.pdf'
const SEND_AS_PDF_FILE_EXTENSIONS = ['docx', 'xlsx']

const removeFileExtension = (filename: string): string => filename.replace(/\.[^/.]+$/, '')

const getFileExtension = (filename?: Nullable<string>): string => (
  filename?.split('.').pop()?.toLowerCase() ?? ''
)

const sanitizeFileNamePart = (value?: Nullable<string>): string => (
  removeFileExtension(value ?? '')
    .replace(/[<>:"/\\|?*]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
)

const templateHasStoredFiles = (draftEnvelope: IDraftEnvelope): boolean => (
  Boolean(
    draftEnvelope.envelopeTemplate &&
    draftEnvelope.templateFiles?.some((file) => !file.fileMustBeProvidedByUser)
  )
)

const getEnvelopeTemplateFields = (draftEnvelope: IDraftEnvelope): IEnvelopeFields | null => (
  draftEnvelope.fieldsIdList.length
    ? draftEnvelope.fieldsIdList.reduce<IEnvelopeFields>((acc, id) => {
        const { key, value } = draftEnvelope.fields[id]

        return {
          ...acc,
          [key]: normalizeEnvelopeFieldValueForRequest(value) as IEnvelopeFields[string]
        }
      }, {})
    : null
)

const getGenerateTemplateDocumentsRequest = (draftEnvelope: IDraftEnvelope) => ({
  fields: getEnvelopeTemplateFields(draftEnvelope)
})

const getPdfFileName = (fileName: string | undefined, fallbackName: string): string => {
  const sanitizedFileName = sanitizeFileNamePart(fileName)

  return `${sanitizedFileName || fallbackName}${GENERATED_TEMPLATE_FILE_EXTENSION}`
}

const createPdfUploadFormData = (pdf: Blob, fileName: string): FormData => {
  const data = new FormData()
  const file = new File([pdf], fileName, { type: 'application/pdf' })

  data.append('file', file)

  return data
}

const shouldSendFileAsPdf = (
  draftEnvelope: IDraftEnvelope,
  fileOguid: string
): boolean => {
  if (draftEnvelope.envelopeTemplate?.oguid) return false

  const fileName = draftEnvelope.preview[fileOguid]?.fileName

  return SEND_AS_PDF_FILE_EXTENSIONS.includes(getFileExtension(fileName))
}

const getPdfFileForSending = async (
  draftEnvelope: IDraftEnvelope,
  fileOguid: string
): Promise<string> => {
  if (!shouldSendFileAsPdf(draftEnvelope, fileOguid)) return fileOguid

  const fileName = draftEnvelope.preview[fileOguid]?.fileName
  const pdfResponse = await ShelfService.getPdf(fileOguid)
  const uploadResponse = await ShelfService.upload(
    createPdfUploadFormData(
      pdfResponse.data,
      getPdfFileName(fileName, fileOguid)
    )
  )

  return uploadResponse.data
}

const getFilesForSending = async (
  draftEnvelope: IDraftEnvelope,
  boxOguid: string,
  filesOverride?: string[]
): Promise<string[] | undefined> => {
  if (draftEnvelope.envelopeTemplate?.oguid) {
    if (filesOverride?.length || !templateHasStoredFiles(draftEnvelope)) return filesOverride

    const response = await BoxesService.generateEnvelopeTemplateDocuments(
      boxOguid,
      draftEnvelope.envelopeTemplate.oguid,
      getGenerateTemplateDocumentsRequest(draftEnvelope)
    )

    return mergeGeneratedTemplateFiles(
      draftEnvelope.templateFiles,
      draftEnvelope.files,
      response.data
    )
  }

  const files = filesOverride ?? draftEnvelope.files
  const hasFilesToConvert = files.some((fileOguid) => shouldSendFileAsPdf(draftEnvelope, fileOguid))

  if (!hasFilesToConvert) return filesOverride

  return await Promise.all(files.map((fileOguid) => getPdfFileForSending(draftEnvelope, fileOguid)))
}

const getGeneratedTemplateFileName = (
  templateFile: NonNullable<IDraftEnvelope['templateFiles']>[number],
  generatedFile: IEnvelopeTemplateGeneratedDocument | undefined,
  generatedIndex: number,
  generatedFilesCount: number
): string => {
  if (generatedFile?.fileName) return generatedFile.fileName

  const templateName = sanitizeFileNamePart(templateFile.fileName)
  const fallbackName = `document${generatedFilesCount > 1 ? ` ${generatedIndex + 1}` : ''}`
  const fileName = templateName || fallbackName

  return `${fileName}${GENERATED_TEMPLATE_FILE_EXTENSION}`
}

const mergeGeneratedTemplateFiles = (
  templateFiles: IDraftEnvelope['templateFiles'],
  providedFiles: string[],
  generatedFiles: IEnvelopeTemplateGeneratedDocument[]
): string[] => {
  let generatedIndex = 0
  let providedIndex = 0

  return (templateFiles ?? []).map((file) => {
    if (file.fileMustBeProvidedByUser) {
      const providedFile = providedFiles[providedIndex]
      providedIndex += 1
      return providedFile
    }

    const generatedFile = generatedFiles[generatedIndex]?.oguid
    generatedIndex += 1
    return generatedFile
  }).filter((file): file is string => Boolean(file))
}

const hasEnvelopeField = <T extends IEnvelope>(envelope: T, field: keyof IEnvelope): boolean =>
  Object.prototype.hasOwnProperty.call(envelope, field)

const getEnvelopeListCacheKey = ({ box, oguid }: IEnvelope): string =>
  `${box.oguid}:${oguid}`

export const mergeEnvelopeListFallback = <T extends IEnvelope>(
  envelope: T,
  listEnvelope?: IEnvelope
): T => {
  if (!listEnvelope) return envelope

  const currentUserTaskOguid = hasEnvelopeField(envelope, 'currentUserTaskOguid')
    ? envelope.currentUserTaskOguid
    : listEnvelope.currentUserTaskOguid
  const hasCurrentTask = Boolean(currentUserTaskOguid)

  return {
    ...envelope,
    allowedMassActions: (
      hasEnvelopeField(envelope, 'allowedMassActions') &&
      (envelope.allowedMassActions?.length || !hasCurrentTask)
    )
      ? envelope.allowedMassActions
      : listEnvelope.allowedMassActions,
    allowedActions: hasEnvelopeField(envelope, 'allowedActions')
      ? envelope.allowedActions
      : listEnvelope.allowedActions,
    currentUserTaskOguid,
    isRead: hasEnvelopeField(envelope, 'isRead')
      ? envelope.isRead
      : listEnvelope.isRead,
    flowState: hasEnvelopeField(envelope, 'flowState')
      ? envelope.flowState
      : listEnvelope.flowState,
    taskCompletedCount: hasEnvelopeField(envelope, 'taskCompletedCount')
      ? envelope.taskCompletedCount
      : listEnvelope.taskCompletedCount,
    taskTotalCount: hasEnvelopeField(envelope, 'taskTotalCount')
      ? envelope.taskTotalCount
      : listEnvelope.taskTotalCount,
  }
}

export const getEnvelopes = createAsyncThunk(
  'envelopes',
  async ({ direction, boxOguid, page, perPage, otherSearchParams }: IEnvelopesListParams, { rejectWithValue, dispatch }) => {
    try {
      const response =
        await EnvelopesService.getEnvelopesList({
          direction, boxOguid, page, perPage, otherSearchParams
        })

      const pagination = getPagination(response.headers)
      dispatch(setPagination(pagination))

      return response.data
    } catch (err) {
      return catchAxiosError(err, rejectWithValue)
    }
  }
)

export const getEnvelope = createAsyncThunk(
  'envelope',
  async (data: IEnvelopeParams, { getState, rejectWithValue }) => {
    try {
      const response =
        await EnvelopesService.getEnvelope(data)

      const envelope = response.data
      const envelopesState = (getState() as AppState).envelopes
      const envelopeFromList = envelopesState.envelopes.data.find(
        ({ oguid, box }) =>
          oguid === envelope.oguid &&
          box.oguid === envelope.box.oguid
      ) ?? envelopesState.envelopesListCache[getEnvelopeListCacheKey(envelope)]

      return mergeEnvelopeListFallback(envelope, envelopeFromList)
    } catch (err) {
      return catchAxiosError(err, rejectWithValue)
    }
  }
)

export const getPreview = createAsyncThunk<IEnvelopeFilePreview | void, IGetPreviewParams, { state: AppState }>(
  'envelopes/getPreview',
  async ({fileUuid}: IGetPreviewParams, { getState, rejectWithValue }) => {
    const {
      draftEnvelope: {
        preview
      }
    } = getState().envelopes

    try {
      return await renderPdfPreview(
        ShelfService.getPdfUrl(fileUuid),
        preview[fileUuid]?.fileName ?? fileUuid
      )
    } catch (err) {
      return catchAxiosError(err, rejectWithValue)
    }
  }
)

export const sendEnvelope = createAsyncThunk<string | void, ISendEnvelopeParams, { state: AppState }>(
  'envelopes/sendEnvelope',
  async ({ boxOguid, files }, { getState, rejectWithValue }) => {
    const {
      draftEnvelope
    } = getState().envelopes

    try {
      const filesForSending = await getFilesForSending(draftEnvelope, boxOguid, files)
      const draftEnvelopeData = getDraftEnvelopeData(draftEnvelope, boxOguid, filesForSending)
      const response = await EnvelopesService.sendEnvelope(draftEnvelopeData)

      window.dispatchEvent(new Event(ACTION_REQUIRED_COUNTS_UPDATED_EVENT))

      return response.data
    } catch (err) {
      return catchAxiosError(err, rejectWithValue)
    }
  }
)

export const generateTemplateDocuments = createAsyncThunk<IGeneratedTemplateDocuments | void, string, { state: AppState }>(
  'envelopes/generateTemplateDocuments',
  async (boxOguid, { getState, rejectWithValue }) => {
    const { draftEnvelope } = getState().envelopes

    if (!draftEnvelope.envelopeTemplate || !templateHasStoredFiles(draftEnvelope)) {
      return {
        files: draftEnvelope.files,
        generatedFiles: []
      }
    }

    try {
      const response = await BoxesService.generateEnvelopeTemplateDocuments(
        boxOguid,
        draftEnvelope.envelopeTemplate.oguid,
        getGenerateTemplateDocumentsRequest(draftEnvelope)
      )

      return {
        files: mergeGeneratedTemplateFiles(draftEnvelope.templateFiles, draftEnvelope.files, response.data),
        generatedFiles: response.data
      }
    } catch (err) {
      return catchAxiosError(err, rejectWithValue)
    }
  }
)

const clearDraftEnvelopeFileData = (
  draftEnvelope: IDraftEnvelope,
  fileUuid: string,
  fileIndex = draftEnvelope.files.findIndex((draftFileUuid) => draftFileUuid === fileUuid)
): void => {
  if (fileIndex < 0) return

  const {
    preview,
    prepositionedMarks,
    prepositionedMarksIdList
  } = draftEnvelope

  const removedMarksIDs = prepositionedMarksIdList.filter((id) => {
    const mark = prepositionedMarks[id]

    return mark.fileIndex === fileIndex
  })

  const newDraftEnvelopePreview = omit(preview, [fileUuid])
  const newPrepositionedMarks = omit(prepositionedMarks, removedMarksIDs)
  const newPrepositionedMarksIdList = prepositionedMarksIdList.filter((id) => !removedMarksIDs.includes(id))

  draftEnvelope.generatedTemplateFiles = null
  draftEnvelope.generatedTemplateFilesSubject = null
  draftEnvelope.preview = {...newDraftEnvelopePreview}
  draftEnvelope.prepositionedMarks = {...newPrepositionedMarks}
  draftEnvelope.prepositionedMarksIdList = newPrepositionedMarksIdList
}

const removeDraftEnvelopeFileData = (
  draftEnvelope: IDraftEnvelope,
  fileUuid: string,
  fileIndex = draftEnvelope.files.findIndex((draftFileUuid) => draftFileUuid === fileUuid)
): void => {
  clearDraftEnvelopeFileData(draftEnvelope, fileUuid, fileIndex)
  draftEnvelope.files = draftEnvelope.files.filter((draftFileUuid) => draftFileUuid !== fileUuid)
}

export const uploadFile = createAsyncThunk<string | void, FormData | IUploadFileParams>(
  'envelopes/uploadFile',
  async (arg, { rejectWithValue }) => {
    try {
      const data = arg instanceof FormData ? arg : arg.data
      const response = await ShelfService.upload(data)

      return response.data
    } catch (err) {
      return catchAxiosError(err, rejectWithValue)
    }
  }
)

export const uploadCommentFile = createAsyncThunk<IDraftEnvelopeCommentFile | void, FormData>(
  'envelopes/uploadCommentFile',
  async (data, { rejectWithValue }) => {
    try {
      const file = data.get('file') as File | null
      const response = await ShelfService.upload(data)

      return {
        oguid: response.data,
        name: file?.name ?? response.data,
        size: file?.size ?? 0,
        mimeType: file?.type ?? ''
      }
    } catch (err) {
      return catchAxiosError(err, rejectWithValue)
    }
  }
)

export const envelopesSlice = createSlice({
  initialState,
  name: 'envelopes',
  reducers: {
    setCheckedEnvelopes: (state, action: PayloadAction<Checkbox>) => {
      state.checkedEnvelopes = { ...state.checkedEnvelopes, ...action.payload }
    },
    replaceCheckedEnvelopes: (state, action: PayloadAction<Checkbox>) => {
      state.checkedEnvelopes = action.payload
      state.isAllEnvelopesSelected = false
    },
    setIsAllEnvelopesSelected: (state, action: PayloadAction<boolean>) => {
      state.isAllEnvelopesSelected = action.payload
    },
    toggleCheckedEnvelope: (state, action: PayloadAction<string>) => {
      state.checkedEnvelopes[action.payload] = !state.checkedEnvelopes[action.payload]
      state.isAllEnvelopesSelected = false
    },
    resetEnvelopes: (state) => {
      state.envelopes.data = [];
      state.isAllEnvelopesSelected = false
      state.currentUserFilterOguid = null
    },
    resetCurrentEnvelope: (state) => {
      state.currentEnvelope = null
    },
    resetCheckedEnvelopes: (state) => {
      state.checkedEnvelopes = {}
      state.isAllEnvelopesSelected = false
    },
    setDirectionEnvelopes: (state, action: PayloadAction<Nullable<EnvelopeDirection | undefined>>) => {
      state.envelopes.direction = action.payload
    },
    setEnvelopeReadState: (state, action: PayloadAction<ISetEnvelopeReadStatePayload>) => {
      const { boxOguid, envelopeOguid, isRead } = action.payload
      const cacheKey = `${boxOguid}:${envelopeOguid}`
      const envelope = state.envelopes.data.find(
        ({ box, oguid }) => box.oguid === boxOguid && oguid === envelopeOguid
      )

      if (envelope) {
        envelope.isRead = isRead
      }

      if (state.envelopesListCache[cacheKey]) {
        state.envelopesListCache[cacheKey].isRead = isRead
      }

      if (
        state.currentEnvelope?.box.oguid === boxOguid &&
        state.currentEnvelope.oguid === envelopeOguid
      ) {
        state.currentEnvelope.isRead = isRead
      }
    },
    removeDraftEnvelopeFile: (state, action: PayloadAction<string>) => {
      removeDraftEnvelopeFileData(state.draftEnvelope, action.payload)
    },
    removeDraftEnvelopeFileAtIndex: (state, action: PayloadAction<number>) => {
      const fileUuid = state.draftEnvelope.files[action.payload]

      if (fileUuid) {
        clearDraftEnvelopeFileData(state.draftEnvelope, fileUuid, action.payload)
      }

      state.draftEnvelope.files[action.payload] = ''
    },
    removeDraftEnvelopeCommentFile: (state, action: PayloadAction<string>) => {
      state.draftEnvelope.commentFiles = state.draftEnvelope.commentFiles
        .filter(({ oguid }) => oguid !== action.payload)
    },
    resetDraftEnvelope: (state) => {
      state.draftEnvelope = { ...initialState.draftEnvelope }
    },
    setDraftEnvelope: (state, action: PayloadAction<IDraftEnvelope>) => {
      state.draftEnvelope = { ...action.payload }
    },
    setDraftEnvelopePreview: (state, action: PayloadAction<ISetDraftEnvelopePreviewPayload>) => {
      const { fileUuid, preview } = action.payload

      state.draftEnvelope.preview[fileUuid] = preview
    },
    addDraftEnvelopeRecipient: (state, action: PayloadAction<boolean>) => {
      const { recipients, recipientsColors, recipientsIdList } = state.draftEnvelope

      const uuid = uuidv4()

      const [ color, ...restRecipientsColors ] = recipientsColors

      const lastRecipient = recipientsIdList.length
        ? recipients[recipientsIdList[recipientsIdList.length - 1]]
        : null
      const groupNum = lastRecipient ? lastRecipient.groupNum + 1 : 1
      const usedStageKeys = new Set(
        recipientsIdList
          .map((id) => recipients[id]?.stageKey)
          .filter((stageKey): stageKey is string => Boolean(stageKey))
      )

      const draftEnvelopeRecipientTemplate: IDraftEnvelopeRecipient = {
        assignmentDescription: null,
        assignedToBoxUserOguid: null,
        boxEmail: '',
        boxPhone: null,
        boxId: '',
        color,
        customParams: [],
        groupNum,
        guestEmail: null,
        isAssignedToBoxUser: action.payload,
        isHostMode: false,
        isRequireSelfie: false,
        isRequireSms: false,
        prepositionedMarks: null,
        signer: {
          fullName: '',
          initials: null,
          position: null,
          phone: null
        },
        signingParams: {
          isAllowElectronicSignature: true,
          isSignOnBehalfOfOrganization: null,
          signerType: DraftEnvelopeRecipientSignerType.PART
        },
        stageKey: getStageRouteKey(null, groupNum - 1, usedStageKeys),
        type: DraftEnvelopeRecipientType.SIGNING
      }

      state.draftEnvelope.recipientsColors = restRecipientsColors

      state.draftEnvelope.recipientsIdList = [
        ...recipientsIdList,
        uuid
      ]

      state.draftEnvelope.recipients = {
        ...recipients,
        [uuid]: {...draftEnvelopeRecipientTemplate}
      }
    },
    removeDraftEnvelopeRecipient: (state, action: PayloadAction<string>) => {
      const {
        prepositionedMarks,
        prepositionedMarksIdList,
        recipients,
        recipientsColors,
        recipientsIdList
      } = state.draftEnvelope

      const { color } = recipients[action.payload]
      const removedMarksIDs = prepositionedMarksIdList.filter((id) => {
        const mark = prepositionedMarks[id]

        return mark.recipient === action.payload
      })

      const newDraftEnvelopeRecipientsIdList = recipientsIdList.filter((uuid) => uuid !== action.payload)
      const newDraftEnvelopeRecipients = omit(recipients, [action.payload])
      const newPrepositionedMarks = omit(prepositionedMarks, removedMarksIDs)
      const newPrepositionedMarksIdList = prepositionedMarksIdList.filter((id) => !removedMarksIDs.includes(id))

      state.draftEnvelope.recipients = {...newDraftEnvelopeRecipients}
      state.draftEnvelope.recipientsColors = [...recipientsColors, color]
      state.draftEnvelope.recipientsIdList = newDraftEnvelopeRecipientsIdList
      state.draftEnvelope.prepositionedMarks = {...newPrepositionedMarks}
      state.draftEnvelope.prepositionedMarksIdList = newPrepositionedMarksIdList
    },
    setDraftEnvelopeRecipients: (state, action: PayloadAction<Record<string, IDraftEnvelopeRecipient>>) => {
      state.draftEnvelope.recipients = action.payload
    },
    setDraftEnvelopeNotifications: (state, action: PayloadAction<IDraftEnvelope['notifications']>) => {
      state.draftEnvelope.notifications = action.payload
    },
    setDraftEnvelopeRecipientColors: (state, action: PayloadAction<string[]>) => {
      state.draftEnvelope.recipientsColors = action.payload
    },
    setDraftEnvelopeRecipientsIdList: (state, action: PayloadAction<string[]>) => {
      state.draftEnvelope.recipientsIdList = action.payload
    },
    addDraftEnvelopePrepositionedMark: (state, action: PayloadAction<IDraftEnvelopePrepositionedMark>) => {
      const { prepositionedMarks, prepositionedMarksIdList } = state.draftEnvelope

      const uuid = uuidv4()

      state.draftEnvelope.prepositionedMarksIdList = [
        ...prepositionedMarksIdList,
        uuid
      ]

      state.draftEnvelope.prepositionedMarks = {
        ...prepositionedMarks,
        [uuid]: {
          ...action.payload,
          isLocked: action.payload.isLocked ?? true
        }
      }
    },
    removeDraftEnvelopePrepositionedMark: (state, action: PayloadAction<string>) => {
      const { prepositionedMarks, prepositionedMarksIdList } = state.draftEnvelope

      const newPrepositionedMarksIdList = prepositionedMarksIdList.filter((uuid) => uuid !== action.payload)
      const newPrepositionedMarks = omit(prepositionedMarks, [action.payload])

      state.draftEnvelope.prepositionedMarks = {...newPrepositionedMarks}
      state.draftEnvelope.prepositionedMarksIdList = newPrepositionedMarksIdList
    },
    setDraftEnvelopePrepositionedMark: (state, action: PayloadAction<ISetDraftEnvelopePrepositionedMark>) => {
      const { prepositionedMarks } = state.draftEnvelope

      const { mark, uuid } = action.payload

      state.draftEnvelope.prepositionedMarks = {
        ...prepositionedMarks,
        [uuid]: {
          ...prepositionedMarks[uuid],
          ...mark
        }
      }
    },
    setDraftEnvelopePrepositionedMarks: (state, action: PayloadAction<Record<string, IDraftEnvelopePrepositionedMark>>) => {
      state.draftEnvelope.prepositionedMarks = action.payload
    },
    setDraftEnvelopePrepositionedMarksIdList: (state, action: PayloadAction<string[]>) => {
      state.draftEnvelope.prepositionedMarksIdList = action.payload
    },
    setDraftEnvelopeMessage: (state, action: PayloadAction<IDraftEnvelopeMessage>) => {
      state.draftEnvelope.message = action.payload
    },
    addDraftEnvelopeField: (state, action: PayloadAction<IDraftEnvelopeField>) => {
      const { fields, fieldsIdList } = state.draftEnvelope

      const uuid = uuidv4()

      state.draftEnvelope.fieldsIdList = [
        ...fieldsIdList,
        uuid
      ]

      state.draftEnvelope.fields = {
        ...fields,
        [uuid]: {...action.payload}
      }
    },
    removeDraftEnvelopeField: (state, action: PayloadAction<string>) => {
      const { fields, fieldsIdList } = state.draftEnvelope

      const newFieldsIdList = fieldsIdList.filter((uuid) => uuid !== action.payload)
      const newFields = omit(fields, [action.payload])

      state.draftEnvelope.fields = {...newFields}
      state.draftEnvelope.fieldsIdList = newFieldsIdList
    },
    setDraftEnvelopeFields: (state, action: PayloadAction<Record<string, IDraftEnvelopeField>>) => {
      state.draftEnvelope.fields = action.payload
      state.draftEnvelope.generatedTemplateFiles = null
      state.draftEnvelope.generatedTemplateFilesSubject = null
    },
    setCurrentBoxOguid: (state, action: PayloadAction<string>) => {
      state.currentBoxOguid = action.payload
    },
    setCurrentUserFilterOguid: (state, action: PayloadAction<Nullable<string>>) => {
      state.currentUserFilterOguid = action.payload
    },
    resetCurrentBoxOguid: (state) => {
      state.currentBoxOguid = ''
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getEnvelopes.fulfilled, (state, action) => {
        if (!action.payload) return

        state.envelopes.data = action.payload
        action.payload.forEach((envelope) => {
          state.envelopesListCache[getEnvelopeListCacheKey(envelope)] = envelope
        })
      })
      .addCase(getEnvelope.fulfilled, (state, action) => {
        const envelope = action.payload

        if (!envelope) return

        const envelopeFromList = state.envelopes.data.find(
          ({ oguid, box }) =>
            oguid === envelope.oguid &&
            box.oguid === envelope.box.oguid
        ) ?? state.envelopesListCache[getEnvelopeListCacheKey(envelope)]

        state.currentEnvelope = mergeEnvelopeListFallback(envelope, envelopeFromList)
      })
      .addCase(uploadFile.fulfilled, (state, action) => {
        if (!action.payload) return

        const { files } = state.draftEnvelope

        const {
          meta: {
            arg
          },
          payload
        } = action

        const data = arg instanceof FormData ? arg : arg.data
        const fileIndex = arg instanceof FormData ? undefined : arg.fileIndex
        const { name } = data.get('file') as File

        if (typeof fileIndex === 'number') {
          const oldFileUuid = files[fileIndex]

          if (oldFileUuid) {
            clearDraftEnvelopeFileData(state.draftEnvelope, oldFileUuid, fileIndex)
          }

          state.draftEnvelope.files[fileIndex] = payload
        } else {
          state.draftEnvelope.files = [ ...files, payload ]
        }

        state.draftEnvelope.generatedTemplateFiles = null
        state.draftEnvelope.generatedTemplateFilesSubject = null
        state.draftEnvelope.preview[payload] = {
          data: [],
          fileName: name,
          isAvailable: true,
          total: 0
        }
      })
      .addCase(uploadCommentFile.fulfilled, (state, action) => {
        if (!action.payload) return

        state.draftEnvelope.commentFiles = [
          ...state.draftEnvelope.commentFiles,
          action.payload
        ]
      })
      .addCase(getPreview.fulfilled, (state, action) => {
        if (!action.payload) return

        const {
          meta: {
            arg: {
              fileUuid
            }
          }
        } = action

        const preview = state.draftEnvelope.preview[fileUuid]

        state.draftEnvelope.preview[fileUuid] = {
          ...preview,
          ...action.payload,
          fileName: preview?.fileName ?? action.payload.fileName
        }
      })
      .addCase(getPreview.rejected, (state, action) => {
        const {
          meta: {
            arg: {
              fileUuid
            }
          }
        } = action

        const preview = state.draftEnvelope.preview[fileUuid]
        if (!preview) return

        state.draftEnvelope.preview[fileUuid] = {
          ...preview,
          isAvailable: false
        }
      })
      .addCase(generateTemplateDocuments.pending, (state) => {
        state.draftEnvelope.generatedTemplateFiles = null
        state.draftEnvelope.generatedTemplateFilesSubject = null
      })
      .addCase(generateTemplateDocuments.fulfilled, (state, action) => {
        if (!action.payload) return

        const {
          files,
          generatedFiles
        } = action.payload

        const { generatedTemplateFiles, preview, templateFiles } = state.draftEnvelope
        const oldGeneratedFiles = generatedTemplateFiles?.filter((file) => !state.draftEnvelope.files.includes(file)) ?? []
        const nextPreview = omit(preview, oldGeneratedFiles) as IDraftEnvelope['preview']

        let generatedIndex = 0

        templateFiles?.forEach((templateFile) => {
          if (templateFile.fileMustBeProvidedByUser) return

          const generatedFile = generatedFiles[generatedIndex]
          const fileOguid = generatedFile?.oguid
          generatedIndex += 1

          if (!fileOguid) return

          const filePreview = nextPreview[fileOguid]

          nextPreview[fileOguid] = {
            data: filePreview?.data ?? [],
            fileName: getGeneratedTemplateFileName(
              templateFile,
              generatedFile,
              generatedIndex - 1,
              generatedFiles.length
            ),
            isAvailable: filePreview?.isAvailable ?? true,
            total: filePreview?.total ?? 0
          }
        })

        state.draftEnvelope.generatedTemplateFiles = files
        state.draftEnvelope.generatedTemplateFilesSubject = state.draftEnvelope.message.subject
        state.draftEnvelope.preview = nextPreview
      })
      .addCase(sendEnvelope.fulfilled, (state) => {
        state.draftEnvelope = {
          ...initialState.draftEnvelope
        }
      })
  }
})

const { actions, reducer } = envelopesSlice

export const {
  setCheckedEnvelopes,
  replaceCheckedEnvelopes,
  setIsAllEnvelopesSelected,
  toggleCheckedEnvelope,
  resetCheckedEnvelopes,
  setDirectionEnvelopes,
  setEnvelopeReadState,
  resetEnvelopes,
  resetCurrentEnvelope,
  removeDraftEnvelopeFile,
  removeDraftEnvelopeFileAtIndex,
  resetDraftEnvelope,
  removeDraftEnvelopeCommentFile,
  setDraftEnvelope,
  setDraftEnvelopePreview,
  addDraftEnvelopeRecipient,
  removeDraftEnvelopeRecipient,
  setDraftEnvelopeNotifications,
  setDraftEnvelopeRecipients,
  setDraftEnvelopeRecipientColors,
  setDraftEnvelopeRecipientsIdList,
  addDraftEnvelopePrepositionedMark,
  removeDraftEnvelopePrepositionedMark,
  setDraftEnvelopePrepositionedMark,
  setDraftEnvelopePrepositionedMarks,
  setDraftEnvelopePrepositionedMarksIdList,
  setDraftEnvelopeMessage,
  addDraftEnvelopeField,
  removeDraftEnvelopeField,
  setDraftEnvelopeFields,
  setCurrentBoxOguid,
  setCurrentUserFilterOguid,
  resetCurrentBoxOguid,
} = actions

export default reducer
